import { defineStore } from 'pinia';
import { useNuxtApp, useRuntimeConfig } from 'nuxt/app';
import { ref } from 'vue';
import { AW_AX_CACHE_GLOBAL } from '~~/common/plugins/axios/awAxCache';
import { useUserInterfaceStore } from '~~/common/stores/userInterface';
import { setErrorNotification } from '~~/common/utils/helper';
import ObjectCache from '~~/common/utils/ObjectCache';
import globalMiddlewareHeaders from '~~/common/server/globalMiddlewareHeaders';
import { transformValidators } from '~~/common/utils/userInterface';
import type { ValidatorOriginal } from '~~/common/config/Validator';
import type { ResponseData, ResponseDataDealer } from 'common/types/api';

type DealerConfigModel = ResponseDataDealer<'/api/v2/config/dealer', 'get'>;
type CommonConfigModel = ResponseData<'/api/v2/config', 'get'>;

// `validators` is narrowed to the FE-side discriminated union, which carries more than
// the schema's ValidatorModel; the rest comes straight from the generated type.
type ConfigResponse = Omit<CommonConfigModel, 'validators'> & Partial<{
  validators: ValidatorOriginal[];
}>;

const commonConfigCache = new ObjectCache<Promise<Omit<ConfigResponse, 'csp'>>>('config_common', '__');
const dealerConfigCache = new ObjectCache<Promise<DealerConfigModel>>('config_dealer', '__');
export const useConfigStore = defineStore('config', () => {
  const nuxtApp = useNuxtApp();
  const config = useRuntimeConfig();
  const userInterfaceStore = useUserInterfaceStore();

  const configCommon = ref<Omit<ConfigResponse, 'csp' | 'validators'> | null>(null);
  const configDealer = ref<DealerConfigModel | null>(null);
  const isSalesForceLogin = ref<boolean | null>(null);

  async function fetchConfigCommon () {
    const { $i18n, $api, $logger, $awRuntimeConfig } = nuxtApp;
    try {
      const cacheKey = ['config', 'shop', $i18n.locale.value];
      commonConfigCache.initCache(cacheKey, async () => {
        const configResponse = await $api.$get<ConfigResponse>('/config', { awAxCache: AW_AX_CACHE_GLOBAL });
        try {
          if (configResponse && typeof configResponse === 'object') {
            if (import.meta.server) {
              const csp = configResponse.csp;
              if (__AW_IS_SHOP__ && Array.isArray(csp) && csp.length && csp.every(isValidCsp)) {
                globalMiddlewareHeaders['Content-Security-Policy'] = (
                  `default-src 'self' 'unsafe-inline' 'unsafe-eval' data: ${csp.map(v => v.url.trim()).join(' ')}`
                );
              } else {
                globalMiddlewareHeaders['Content-Security-Policy'] = null;
              }
            }
          }
        } catch (err) {
          $logger.error(err);
        }
        const { csp, ...configResponseWithoutCsp } = configResponse;
        return configResponseWithoutCsp;
      });
      const cached = (await commonConfigCache.getCache(cacheKey)?.value);
      if (!cached) {
        return;
      }
      const { validators, ...configWithoutValidators } = cached;
      if (validators) {
        const vals = transformValidators(validators);
        userInterfaceStore.setValidators(vals);
      }
      configCommon.value = configWithoutValidators;
      const persistentConfig = configWithoutValidators.persistentConfig;

      const KEY_IS_SALESFORCE_LOGIN_ENABLED = 'salesforce_login_enabled';
      if (
        config.public.auchanIsCustomerDiamondApiControlEnabled
          && persistentConfig
          && (KEY_IS_SALESFORCE_LOGIN_ENABLED in persistentConfig)
      ) {
        isSalesForceLogin.value = persistentConfig[KEY_IS_SALESFORCE_LOGIN_ENABLED] ?? null;
      } else {
        isSalesForceLogin.value = $awRuntimeConfig.authMode === 'CD';
      }
    } catch (error) {
      $logger.error('error', error);
      setErrorNotification({ nuxtApp, error, manualClose: true });
    }
  }

  async function fetchConfigDealer () {
    const { $i18n, $api, $logger } = nuxtApp;
    try {
      const cacheKey = ['config', 'dealer', $i18n.locale.value];
      dealerConfigCache.initCache(cacheKey, async () => {
        return await $api.$get<DealerConfigModel>('config/dealer');
      });
      const cached = await dealerConfigCache.getCache(cacheKey)?.value;
      if (!cached) {
        return;
      }
      configDealer.value = cached;
    } catch (error) {
      $logger.error(error);
      setErrorNotification({ nuxtApp, error, manualClose: true });
    }
  }

  return {
    configCommon,
    configDealer,
    isSalesForceLogin,
    fetchConfigCommon,
    fetchConfigDealer,
  };
});

function isValidCsp (val: unknown): val is { url: string } {
  return (typeof val === 'object' && !!val) && ('url' in val) && (typeof val?.url === 'string');
}
